/**
 * Subway-map diagram layout for the Timeline view — a pure function over
 * `PreparationStep[]` (already stepOrder-sorted) plus their real schedule
 * (schedule.ts's computeSchedule() output, same order/length), same
 * spirit as recipe_renderer/schedule.ts: no DOM, independently
 * verifiable. The LAYOUT itself (x-positions, spacing, row packing)
 * stays entirely schematic — uniform per step, not proportional to real
 * duration — since TimelineView's own rendering cares about topological
 * structure (step order, and which steps happen *while* other steps are
 * going on), not proportional duration. The one place real time IS
 * consulted is deciding whether a dormant track is actually eligible to
 * be reactivated (see the track-reactivation bullet below) — everywhere
 * else stays exactly as time-agnostic as before.
 *
 * Model — a flat set of "tracks," not a tree: one preparer still walks
 * `steps` strictly in order (same single-cursor assumption as
 * schedule.ts). There's always exactly one ACTIVE track, which receives
 * ordinary placements by default:
 *
 * - A step whose attention is shorter than its own duration keeps
 *   "cooking" after the preparer moves on — right after it's placed, a
 *   NEW track opens and becomes active, so subsequent steps continue
 *   there by default (this is what keeps the longer, continuing
 *   narrative flowing forward rather than getting stuck "inside" the
 *   short background task).
 * - A step that depends on a node sitting in some OTHER, currently open,
 *   non-active track is placed on THAT track instead — but only if the
 *   track is actually REACTIVATION-ELIGIBLE: its own most-recently-
 *   placed item must have REALLY finished (real schedule finishSeconds)
 *   by the time this step REALLY starts. Checking the specific matched
 *   dependency node itself would never filter anything out —
 *   schedule.ts already guarantees any real dependency has finished by
 *   the time its consumer starts — the thing that can still be
 *   mid-flight is whatever ELSE got placed on that same track
 *   afterward (e.g. a 30-minute bake started right after the actual
 *   matched node). A track that fails this check just isn't a
 *   candidate this time — the step falls back to the active track
 *   instead (or to a better-timed match, if more than one applies) —
 *   and the ineligible track is left open, untouched, for a later step
 *   to reactivate once it truly has become available. This is also why
 *   a step that ALSO happens to depend on the active track's own
 *   output never gets dragged away from it: the active track was never
 *   a "jump" candidate in the first place (it's where things already
 *   are), so there's nothing for the eligibility check to override.
 * - A track that opened but never received a single item (its own
 *   trigger's immediate next consumer depended on it directly, so
 *   nothing was ever placed before it was reclaimed) is dropped
 *   entirely — there's nothing to draw.
 *
 * Why this still can't produce a crossing connector: only one track is
 * ever active, and a track only ever gains new items by BEING the
 * active track or by being a jump's target (which makes it active on
 * the spot) — the eligibility check only changes WHICH track (if any)
 * gets picked as that target, not this invariant. So a track left
 * open-but-not-reactivated during some other jump is frozen — nothing
 * can extend it — meaning its rendered x-span can never grow to overlap
 * whatever a LATER jump's connector needs to cross through. Verified by
 * hand against several constructed multi-level reactivation scenarios
 * (see this module's own test coverage), not just asserted.
 *
 * One real cost of the eligibility check: a step's dependency on a
 * track that fails it (and isn't picked some other way) gets no visual
 * trace at all in the diagram — not even the old "same line, further
 * upstream" hint, since the step never lands on that track. The
 * underlying data/step text is unaffected either way; this is purely a
 * gap in the schematic diagram.
 *
 * Multiple simultaneous dependencies (a step whose `children` include
 * nodes from more than one currently open, non-active, ELIGIBLE track):
 * the target is whichever of them opened earliest (lowest id — true
 * final row isn't known until the row-assignment pass that runs once
 * the whole walk is done, but creation order is a good proxy and
 * matches every case checked so far); every other matched-and-eligible
 * track, and the previously active one if different from the target,
 * each draw their own "merge" connector into the same landing node —
 * multiple lines converging on one node, same as a real interchange
 * station.
 *
 * A node's own 2nd/3rd transformation usage is NOT a special case here
 * any more: callers are expected to already enforce that only a node's
 * LAST usage can ever be parallelizable (see NodeVisualizer.ts's
 * Attention field gating and the backend normalization in write.py) —
 * so an earlier usage's `attentionDuration` is always exactly its
 * `duration`, and it's placed exactly like any other ordinary step.
 */
import type { PreparationStep } from '../recipe_renderer/RecipeTextRenderer.js';
import type { ScheduledStep } from '../recipe_renderer/schedule.js';

export interface DiagramNode {
  step: PreparationStep;
  track: DiagramTrack;
  /** Integer sequence slot within `track` — NOT pixels; TimelineView
   *  multiplies by its own fixed node spacing. */
  x: number;
}

export interface DiagramTrack {
  id: number;
  /** The node whose parallelism opened this track — null for the very
   *  first (main) track. Informational only (e.g. for debugging):
   *  dependency matching below keys off `trackByNodeId`, not this, so
   *  a dependency on a LATER node reactivated onto this track (not
   *  just this original trigger) is still found correctly. */
  triggerNodeId: string | null;
  /** This track's own nodes, append-only, in placement (== x) order. */
  items: DiagramNode[];
  /** Next free x-slot on this track — internal bookkeeping. */
  cursor: number;
  /** Assigned by the row-packing pass below; 0 for the main track. */
  row: number;
}

export interface DiagramConnector {
  kind: 'split' | 'merge';
  from: DiagramNode;
  to: DiagramNode;
}

export interface DiagramLayout {
  /** Same order as the input `steps`. */
  nodes: DiagramNode[];
  /** Every non-empty track — the first one (id 0) is always the main
   *  track the very first step lands on. */
  tracks: DiagramTrack[];
  connectors: DiagramConnector[];
}

export function computeDiagramLayout(steps: PreparationStep[], scheduled: ScheduledStep[]): DiagramLayout {
  let nextTrackId = 0;
  const makeTrack = (triggerNodeId: string | null, cursorStart: number): DiagramTrack => ({
    id: nextTrackId++,
    triggerNodeId,
    items: [],
    cursor: cursorStart,
    row: 0,
  });

  const allTracks: DiagramTrack[] = [];
  const main = makeTrack(null, 0);
  allTracks.push(main);

  const openTracks: DiagramTrack[] = [main];
  let activeTrack = main;
  // Tracks that opened but haven't received their first item yet —
  // holds the node to draw the eventual "split" connector FROM, once
  // (if ever) that first item actually lands. See this module's own
  // doc comment on why an immediately-reclaimed track is just dropped.
  const pendingSplitFrom = new Map<number, DiagramNode>();
  // Which track currently "owns" each node's output — NOT just each
  // track's own original trigger node: once a track is reactivated and
  // gains a later item, a dependency on THAT later node must find the
  // track too, not only a dependency on the track's very first item.
  // (A step depending on a node whose track has ALREADY been closed by
  // the time this step runs doesn't need any special handling — that
  // track's work already fed back into the flow earlier; real
  // dependency ordering guarantees it's available regardless of which
  // track happens to have drawn it.)
  const trackByNodeId = new Map<string, DiagramTrack>();
  // Real finishSeconds of whatever was most recently placed on each
  // track, keyed by track id — NOT the specific node some later step
  // depends on; see this module's own doc comment on why the track's
  // own frontier is what must be checked for reactivation eligibility.
  const frontierFinishByTrack = new Map<number, number>();

  const nodes: DiagramNode[] = [];
  const connectors: DiagramConnector[] = [];

  const place = (step: PreparationStep, track: DiagramTrack, finishSeconds: number): DiagramNode => {
    const isFirstItem = track.items.length === 0;
    const node: DiagramNode = { step, track, x: track.cursor };
    track.cursor += 1;
    track.items.push(node);
    nodes.push(node);
    trackByNodeId.set(step.node.id, track);
    frontierFinishByTrack.set(track.id, finishSeconds);
    if (isFirstItem) {
      const splitFrom = pendingSplitFrom.get(track.id);
      if (splitFrom) {
        connectors.push({ kind: 'split', from: splitFrom, to: node });
        pendingSplitFrom.delete(track.id);
      }
    }
    return node;
  };

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const childIds = step.node.data.children ?? [];
    const myStartSeconds = scheduled[i].startSeconds;
    const matchedTracks = new Set<DiagramTrack>();
    for (const childId of childIds) {
      const t = trackByNodeId.get(childId);
      if (!t || t === activeTrack || !openTracks.includes(t)) continue;
      const frontierFinish = frontierFinishByTrack.get(t.id) ?? -Infinity;
      if (frontierFinish <= myStartSeconds) matchedTracks.add(t);
    }
    const matches = [...matchedTracks];

    let target = activeTrack;
    const toClose: DiagramTrack[] = [];
    if (matches.length > 0) {
      target = matches.reduce((a, b) => (a.id < b.id ? a : b));
      for (const t of matches) {
        if (t !== target) toClose.push(t);
      }
      if (activeTrack !== target) toClose.push(activeTrack);
    }

    // Catch the target's cursor up to every track being folded into it
    // — same "never let one branch's ending outrun where everything
    // reconnects" idea as this session's earlier Gantt-chart cross-row
    // fix, just counting nodes instead of seconds. Without this, the
    // new item could land at an x EARLIER than a track it's merging —
    // e.g. a track reactivated via its own distant original trigger
    // while a nested track (opened and closed entirely in between) has
    // already pushed further ahead — producing a backwards,
    // right-to-left merge connector.
    if (toClose.length > 0) {
      target.cursor = Math.max(target.cursor, ...toClose.map((t) => t.cursor));
    }

    const node = place(step, target, scheduled[i].finishSeconds);

    for (const t of toClose) {
      // A track being closed here can still be empty — rule 4's "empty
      // track" case isn't only "the very next step depends on the
      // trigger directly": the active (and therefore about-to-close)
      // track can ALSO just be the newly-opened, still-empty one,
      // closed purely as a side effect of an unrelated jump elsewhere
      // (e.g. the step right after a parallelizable trigger depends on
      // some OTHER, earlier track, not on the trigger itself). Either
      // way, nothing was ever drawn on it — no connector to draw, and
      // it's dropped by the end-of-walk filter below regardless.
      if (t.items.length > 0) {
        connectors.push({ kind: 'merge', from: t.items[t.items.length - 1], to: node });
      }
      const idx = openTracks.indexOf(t);
      if (idx !== -1) openTracks.splice(idx, 1);
    }
    activeTrack = target;

    const duration = step.duration ?? 0;
    const attention = step.attentionDuration ?? duration;
    if (duration > 0 && attention < duration) {
      const newTrack = makeTrack(step.node.id, node.x + 1);
      allTracks.push(newTrack);
      openTracks.push(newTrack);
      pendingSplitFrom.set(newTrack.id, node);
      activeTrack = newTrack;
    }
  }

  // Rule 4: a track that opened but never received an item (its
  // trigger's own immediate next consumer depended on it directly) has
  // nothing to draw — drop it. Nothing else ever references it (no
  // connector is created until a track's first item actually lands).
  const tracks = allTracks.filter((t) => t.items.length > 0);
  assignRows(tracks);

  return { nodes, tracks, connectors };
}

/**
 * Flat greedy "minimum meeting rooms" row assignment across every
 * track's own `[firstItem.x, lastItem.x + 1)` span — the exact same
 * algorithm schedule.ts's own assignRows() uses for real time, just
 * over integer x-slots. No parent/child recursion needed: tracks
 * aren't hierarchical any more, so whichever track is free earliest
 * (by x) reuses the lowest available row, same as any other interval-
 * packing problem.
 */
function assignRows(tracks: DiagramTrack[]): void {
  const byStart = [...tracks].sort((a, b) => xStartOf(a) - xStartOf(b));
  const rowFreeAt: number[] = [];
  for (const track of byStart) {
    const start = xStartOf(track);
    const end = xEndOf(track);
    let row = rowFreeAt.findIndex((freeAt) => freeAt <= start);
    if (row === -1) {
      row = rowFreeAt.length;
      rowFreeAt.push(end);
    } else {
      rowFreeAt[row] = end;
    }
    track.row = row;
  }
}

function xStartOf(track: DiagramTrack): number {
  return track.items[0].x;
}

function xEndOf(track: DiagramTrack): number {
  return track.items[track.items.length - 1].x + 1;
}
